import { useState, useRef } from 'react'
import { useParams } from 'react-router'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDataset, useDatasetData } from '@hooks/useCatalog'
import SpreadsheetTable from '@components/SpreadsheetTable/SpreadsheetTable'
import { useTableHighlight } from 'src/hooks/useTableHighlight'
import { DataRow } from 'src/types/tableDataSet'
import TableSkeleton from '@components/skeletons/TableSkeleton/TableSkeleton'

import './TableDataSet.css'
import { useTrialGuard } from '@hooks/useTrialGuard'

export default function TableDataSet() {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const { guard } = useTrialGuard()

  const [searchQuery, setSearchQuery] = useState('')
  const tableRef = useRef<HTMLDivElement>(null)
  useTableHighlight(tableRef, searchQuery)

  const { dataset, colSpec } = useDataset(id)
  const { data, pagination } = useDatasetData(id)
  const noData = data.length === 0

  if (!dataset.title || !dataset.id) {
    return (
      <div id="table-data-set">
        <div className="container">
          <h1>
            <FormattedMessage id="dataset.table-data.empty" />
          </h1>
        </div>
      </div>
    )
  }

  const rows: DataRow[] = noData
    ? []
    : data.map((row) => colSpec.map((col) => row[col.name] ?? null))

  const handleDownload = () => {
    window.open(`/data-sets/${id}/download`, '_blank')
  }

  return (
    <div id="table-data-set">
      <header>
        <h1>
          <span className="icon icon-table" />
          <span>{dataset.title}</span>
        </h1>
        <section className="wrapper">
          <button
            disabled={noData}
            onClick={guard(handleDownload)}
            className="action-button"
          >
            <span className="icon icon-download" />
            <span>
              <FormattedMessage id="download" />
            </span>
          </button>
          <search className="search-highlight">
            <input
              disabled={noData}
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'search-data.placeholder'
              })}
            />
          </search>
        </section>
      </header>
      <div className="table-wrapper" ref={tableRef}>
        {noData ? (
          <div id="table-data-set" className="empty-data">
            <h2>
              <FormattedMessage id="dataset.table-data.empty" />
            </h2>
            <TableSkeleton rowCount={5} />
          </div>
        ) : (
          <SpreadsheetTable
            columnsDef={colSpec}
            rows={rows}
            pageSize={pagination?.itemsPerPage}
            currentPage={pagination?.page}
            idColumnIndex={0}
          />
        )}
      </div>
    </div>
  )
}
